import { Box, Button, Divider, SelectChangeEvent, Stack } from '@mui/material'
import { LassoTextField } from '../../../components/LassoTextField'
import LassoSelect from '../../../components/LassoSelect'
import { useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { SshDestinationLocation, useAddDestinationLocationMutation, useGetDataProvidersQuery } from '../../../api/dataProvidersApiSlice';
import { useNavigate } from 'react-router-dom';
import ConfigurationInformation from './ConfigurationInformation';

type Props = {
    setFormType: any;
}

const SSHConfigForm = ({ setFormType }: Props) => {
    const { data } = useGetDataProvidersQuery({});
    const [addDestinationLocation] = useAddDestinationLocationMutation();
    const navigate = useNavigate();
    const groups = data?.meta?.groups;
    const [disableFields] = useState(false);

    const {
        handleSubmit,
        control,
        reset,
        setError,
        formState: { errors },
    } = useForm<SshDestinationLocation>({
        defaultValues: {
            name: '',
            type: 'UserkeyFlatDirSshDataProvider',
            hostName: '',
            userName: '',
            path: '',
            port: '',
            project: data?.meta?.groups[0].id || "", //Necessary to set the Select component as controlled as opposed to uncontrolled.
        }
    })

    const handleChange = (event: SelectChangeEvent<unknown> , field: ControllerRenderProps<SshDestinationLocation, "type">) => {
        setFormType(event.target.value as string)
        field.onChange(event)
    }

    const onSubmit = (data: SshDestinationLocation) => {

        addDestinationLocation(data)
            .unwrap()
            .then(() => {
                reset();
                navigate('/file-download');
            })
            .catch((error) => {
                if (error.status === 400) {
                    Object.keys(error.data || {}).forEach((field: string) => {
                        if (field === 'name') {
                            setError('name', { type: 'manual', message: error.data[field] })
                        }
                    })
                }
            });
    }

    return (
        <Stack direction="column" gap={4}>
            <Stack sx={{ flexDirection: { xs: "column", md: "row" } }} gap={4}>
                <Controller
                    name="name"
                    control={control}
                    rules={{
                        required: "This is required",
                        validate: {
                            noSpaces: v => !(v.indexOf(' ') >= 0) || "Name cannot have spaces"
                        }
                    }}
                    render={({ field }) =>
                        <LassoTextField
                            sx={{ flexGrow: 1 }}
                            label="Name"
                            error={'name' in errors}
                            helperText={errors?.name?.message || ""}
                            {...field}

                        />}
                />
                <Controller
                    name="type"
                    control={control}
                    render={({ field }) =>
                        <LassoSelect
                            sx={{ minWidth: "200px" }}
                            options={[{ label: "SSH", value: "UserkeyFlatDirSshDataProvider" }, { label: "S3 Bucket", value: "S3MultiLevelDataProvider" }]}
                            label="Destination Type"
                            {...field}
                            onChange={(e) => {handleChange(e, field)}}
                        />}
                />
            </Stack>

            <Divider />
                    
            <Stack direction="column" gap="40px">
                <Stack sx={{ flexDirection: { xs: "column", md: "row" } }} gap={4}>

                    <Controller
                        name="hostName"
                        control={control}
                        rules={{ required: "This is required." }}
                        render={({ field }) =>
                            <LassoTextField
                                label="Hostname"
                                fullWidth
                                disabled={disableFields}
                                error={'hostName' in errors}
                                helperText={errors?.hostName?.message || ""}
                                {...field}
                            />}
                    />
                    <Controller
                        name="userName"
                        control={control}
                        rules={{ required: "This is required." }}
                        render={({ field }) =>
                            <LassoTextField
                                label="Username"
                                fullWidth
                                disabled={disableFields}
                                error={'userName' in errors}
                                helperText={errors?.userName?.message || ""}
                                {...field}
                            />}
                    />
                </Stack>
                <Stack direction="row" gap={4} sx={{ flexDirection: { xs: "column", md: "row" } }}>
                    <Controller
                        name="path"
                        control={control}
                        rules={{ required: "This is required." }}
                        render={({ field }) =>
                            <LassoTextField
                                label="Path"
                                fullWidth
                                disabled={disableFields}
                                error={'path' in errors}
                                helperText={errors?.path?.message || ""}
                                {...field}
                            />}
                    />
                    <Controller
                        name="port"
                        control={control}
                        render={({ field }) =>
                            <LassoTextField
                                label="Port"
                                type="number"
                                fullWidth
                                disabled={disableFields}
                                {...field}
                            />}
                    />
                </Stack>
                <Box>
                    <Controller
                        name="project"
                        control={control}
                        render={({ field }) =>
                            <LassoSelect
                                options={groups?.map((group) => { return { value: group.id, label: group.name } })}
                                label="Choose a Project"
                                fullWidth
                                disabled={disableFields}
                                {...field}
                            />}
                    />
                </Box>
                <Button
                    disableRipple
                    variant="primary"
                    size="small"
                    sx={{ alignSelf: "center" }}
                    onClick={handleSubmit(onSubmit)}
                >
                    Add destination
                </Button>
            </Stack>
            <ConfigurationInformation />
        </Stack>
    )
}

export default SSHConfigForm